import React, { useState, useEffect, Fragment } from 'react'
import { useIdleTimer } from 'react-idle-timer'
import { Transition, Dialog } from '@headlessui/react'
import Button from '@/components/common/Button'
import { logout } from '@/utils/authConfig'

const IdleTimer = ({ user }) => {
  const promptTimeout = 1000 * 30

  // Modal open state
  const [open, setOpen] = useState(false)

  // Set timeout values
  const [timeout, setTimeout] = useState(Number(process.env.NEXT_PUBLIC_IDLE_TIMEOUT))

  // Time before idle
  const [remaining, setRemaining] = useState(0)

  const onPrompt = () => {
    setOpen(true)
    setRemaining(promptTimeout)
  }

  const onIdle = () => {
    // onIdle will be called after the promptTimeout is reached.
    // In this case 30 seconds. Here you can close your prompt and
    // perform log out user.
    // Events will be rebound as long as `stopOnMount` is not set.
    setOpen(false)
    setRemaining(0)
    logout()
  }

  const onActive = () => {
    // onActive will only be called if `reset()` is called while `isPrompted()`
    // is true. Here you will also want to close your modal and perform
    // any active actions.
    setOpen(false)
    setRemaining(0)
  }

  const { getRemainingTime, isPrompted, activate } = useIdleTimer({
    timeout,
    promptTimeout,
    onPrompt,
    onIdle,
    onActive,
  })

  const handleStillHere = () => {
    setOpen(false)
    activate()
  }

  useEffect(() => {
    if (user?.length > 0) {
      const userDetails = user[0]
      const isUser = userDetails?.idTokenClaims?.email?.endsWith('@unitedtalent.com')
      isUser && setTimeout(Number(process.env.NEXT_PUBLIC_INTERNAL_IDLE_TIMEOUT))
    }
  }, [user])

  useEffect(() => {
    const interval = setInterval(() => {
      if (isPrompted()) {
        setRemaining(Math.ceil(getRemainingTime() / 1000))
      }
    }, 1000)
    return () => {
      clearInterval(interval)
    }
  }, [getRemainingTime, isPrompted])

  return (
    <>
      <Transition appear show={open} as={Fragment}>
        <Dialog as="div" className="relative z-10" onClose={() => null}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-black bg-opacity-25" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden bg-black p-6 text-left align-middle shadow-xl transition-all">
                  <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-white">
                    You Have Been Idle!
                  </Dialog.Title>
                  <p className="py-4 border-t">Logging you out in {remaining} seconds</p>
                  <div className="flex flex-wrap items-center gap-10 mt-2 mb-1 ml-4">
                    <Button
                      className="px-8 py-3 text-sm font-normal"
                      label={'Stay'}
                      handleClick={handleStillHere}
                    />
                    <Button
                      className="py-3 px-8 border border-white tagline-font tracking-wider text-sm font-bold button-hover"
                      label={'Logout'}
                      handleClick={logout}
                    />
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </>
  )
}

export default IdleTimer
